/**
 * Consulta com fichas ainda não salvas — em QUALQUER aba deste navegador.
 *
 * O token de sessão fica no localStorage, compartilhado entre as abas: um
 * "Sair" em qualquer aba derruba a sessão da aba onde a consulta está aberta.
 * Caso Calheira (06/10): o logout em outra aba barrou as fichas da consulta e o
 * que a médica digitou depois não chegou ao banco (card 17ttbukv5jz).
 *
 * A consulta 3.0 (PersistenceContext) registra um respondedor; o "Sair"
 * pergunta antes de encerrar a sessão. O respondedor força o salvamento do que
 * acabou de ser digitado e aguarda a fila antes de responder — por isso a
 * resposta é assíncrona. A fila vive na aba da consulta (sessionStorage),
 * então a pergunta vai pelo BroadcastChannel, com resposta direta quando a
 * consulta está na própria aba.
 *
 * Protocolo do canal:
 *  - `query {id, tab}`: o "Sair" pergunta (a própria aba não responde);
 *  - `pending {id, from}`: uma aba com consulta avisa que vai responder (logo);
 *  - `result {id, from, unsaved}`: a resposta depois do salvamento forçado.
 */

const CHANNEL = 'eyecare-attendance-unsaved'

const localResponders = new Set()

// Identifica esta aba: a pergunta do próprio "Sair" já é respondida pelos
// respondedores locais e não pode voltar pelo canal (salvaria duas vezes).
const TAB_ID = `${Date.now()}-${Math.random()}`

/**
 * Registra quem sabe responder se há consulta com dado não salvo nesta aba.
 * Retorna a função de cancelamento.
 *
 * @param {() => boolean | Promise<boolean>} hasUnsaved
 * @returns {() => void}
 */
export function registerUnsavedAttendance(hasUnsaved) {
  localResponders.add(hasUnsaved)

  let channel = null
  if (typeof BroadcastChannel !== 'undefined') {
    const from = `${Date.now()}-${Math.random()}`
    channel = new BroadcastChannel(CHANNEL)
    channel.onmessage = ({ data }) => {
      if (data?.type !== 'query' || data.tab === TAB_ID) return
      channel.postMessage({ type: 'pending', id: data.id, from })
      Promise.resolve()
        .then(() => hasUnsaved())
        .catch(() => true) // na dúvida, conta como não salvo
        .then(unsaved => {
          try {
            channel.postMessage({ type: 'result', id: data.id, from, unsaved })
          } catch {
            // canal fechado (consulta saiu no meio): nada a responder
          }
        })
    }
  }

  return () => {
    localResponders.delete(hasUnsaved)
    if (channel) channel.close()
  }
}

/**
 * Há consulta 3.0 aberta NESTA aba? Com a consulta aberta, um 401 não leva a
 * aba para /login (P6 do card 17ttbukv5jy): o aviso da consulta pede o novo
 * login sem tirar a médica da tela, e as fichas esperam o login de volta.
 *
 * @returns {boolean}
 */
export function isAttendanceOpen() {
  return localResponders.size > 0
}

/**
 * Há consulta com fichas não salvas nesta aba ou em outra? Antes de responder,
 * cada consulta força o salvamento do que acabou de ser digitado.
 *
 * - `ackTimeoutMs`: quanto esperar outra aba dizer que tem consulta aberta;
 * - `resultTimeoutMs`: quanto esperar o resultado depois disso. Sem resultado
 *   a tempo, conta como não salvo (o "Sair" pede confirmação por segurança).
 *
 * @param {{ ackTimeoutMs?: number, resultTimeoutMs?: number }} [options]
 * @returns {Promise<boolean>}
 */
export async function hasUnsavedAttendance({
  ackTimeoutMs = 400,
  resultTimeoutMs = 6000
} = {}) {
  for (const hasUnsaved of localResponders) {
    try {
      if (await hasUnsaved()) return true
    } catch {
      return true
    }
  }
  if (typeof BroadcastChannel === 'undefined') return false

  return new Promise(resolve => {
    const id = `${Date.now()}-${Math.random()}`
    const channel = new BroadcastChannel(CHANNEL)
    // Abas que avisaram que têm consulta e ainda não mandaram o resultado.
    const waiting = new Set()
    // "Tudo salvo" só depois da janela de aviso: uma aba que responde rápido
    // não pode encerrar a pergunta antes de outra avisar que tem consulta.
    let ackClosed = false
    let resultTimer = null
    const done = result => {
      clearTimeout(ackTimer)
      clearTimeout(resultTimer)
      channel.close()
      resolve(result)
    }
    const ackTimer = setTimeout(() => {
      ackClosed = true
      if (waiting.size === 0) done(false)
    }, ackTimeoutMs)
    channel.onmessage = ({ data }) => {
      if (data?.id !== id) return
      if (data.type === 'pending') {
        waiting.add(data.from)
        if (!resultTimer) {
          resultTimer = setTimeout(() => done(true), resultTimeoutMs)
        }
      } else if (data.type === 'result') {
        if (data.unsaved) return done(true)
        waiting.delete(data.from)
        if (ackClosed && waiting.size === 0) done(false)
      }
    }
    channel.postMessage({ type: 'query', id, tab: TAB_ID })
  })
}
